// Copyright ©️ 2026 eVolve MEP, LLC

import './spooling.css';

import { useCallback, useEffect, useMemo } from 'react';

import { type AutodeskCloudModel, FLAT_BLACK_FILLER } from '@evolvemep/api-ts';
import { Accordion, Divider, Flex, Loader, Stack, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { CloudSpoolingHelpLink } from 'components/Autodesk/constants';
import { getDbIdsFromSpool } from 'components/Autodesk/extensions/SpoolsExtension/helpers';
import type { RenderableSpool, SpoolingSettings } from 'components/Autodesk/extensions/SpoolsExtension/types';
import { useAutodeskExtensionStoreSnapshot } from 'components/Autodesk/useAutodeskExtensionStore';
import { TextInputDebounced } from 'components/Mantine/TextInputDebounced';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { strongValues } from 'helpers/strongEntries';
import { AssignSpoolToWorkOrder } from 'modules/Shop/Spooling/Assigning/AssignSpoolToWorkOrder';
import { useSpoolFilters } from 'modules/Shop/Spooling/hooks/useSpoolFilters';
import { useSpoolRequestsViewerState } from 'modules/Shop/Spooling/hooks/useSpoolRequestsViewerState';

import { useSpoolAssignment } from '../hooks/useSpoolAssignment';
import { RequestedSpools } from '../SpoolRequests/RequestedSpools';
import { AssignedSpools } from './AssignedSpools';
import { SpoolFiltersPopover } from './SpoolFiltersPopover';
import { UnassignedSpools } from './UnassignedSpools';

type Props = {
  spoolingSettings: SpoolingSettings;
  requestingNewSpools: boolean;
  model: AutodeskCloudModel | null;
};

export const SpoolAssignmentPane = ({ spoolingSettings, requestingNewSpools, model }: Props) => {
  const {
    spoolRequests,
    visibleSpoolRequests,
    colorScheme: spoolRequestColorScheme,
    loadingSpoolRequests,
  } = useSpoolRequestsViewerState();
  const { commands } = useAutodeskExtensionStoreSnapshot('apsViewer');
  const spools = useAutodeskExtensionStoreSnapshot('spools');
  const spoolValues = useMemo(() => strongValues(spools ?? {}), [spools]);

  const getSpoolsFromSpoolNames = useCallback(
    (spoolNames: RenderableSpool['name'][]): RenderableSpool[] => {
      if (isNil(spools)) return [];
      return spoolNames.map((spoolName) => spools[spoolName]).filter(isNotNil);
    },
    [spools],
  );

  const {
    assignedSpools,
    unassignedSpools,
    loadingAssignedSpools,
    loadingSpoolsReadyForFabrication,
    refreshSpools,
    unassignedSpoolColorScheme,
    assignedSpoolColorScheme,
    selectedSpools,
    spoolVisibility,
    resetSelectedSpools,
  } = useSpoolAssignment();

  const {
    searchPhrase,
    setSearchPhrase,
    spoolFilter,
    setSpoolFilter,
    filteredRequestedSpools,
    filteredAssignedSpools,
    filteredUnassignedSpools,
  } = useSpoolFilters();

  const spoolsRealized = useMemo(() => isNotNil(spools) && !loadingAssignedSpools, [loadingAssignedSpools, spools]);

  const { fitToView: shouldFitToView, colorKeyEnabled } = spoolingSettings;

  // Apply the color scheme to the model on change
  useEffect(() => {
    if (!spoolsRealized) return;

    commands?.clearThemingColors();
    if (!colorKeyEnabled) return;

    unassignedSpools.forEach(({ name, objects }) => {
      const color = unassignedSpoolColorScheme[name] ?? FLAT_BLACK_FILLER;
      const dbIds = objects.map(({ dbId }) => dbId);
      commands?.setThemingColor(dbIds, color);
    });

    assignedSpools.forEach(([workOrderId, mappedSpools]) => {
      const color = assignedSpoolColorScheme[workOrderId] ?? FLAT_BLACK_FILLER;
      const dbIds = mappedSpools.flatMap((spool) => spool.objects.map(({ dbId }) => dbId));
      commands?.setThemingColor(dbIds, color);
    });

    spoolRequests?.forEach(([spoolId, { objects }]) => {
      const color = spoolRequestColorScheme[spoolId] ?? FLAT_BLACK_FILLER;
      const dbIds = objects.map(({ dbId }) => dbId);
      commands?.setThemingColor(dbIds, color);
    });
  }, [
    commands,
    spoolsRealized,
    colorKeyEnabled,
    unassignedSpoolColorScheme,
    unassignedSpools,
    assignedSpoolColorScheme,
    assignedSpools,
    spoolRequestColorScheme,
    spoolRequests,
  ]);

  // Isolate the spools that match our filter and are visible unless we're requesting a new spool.
  const filteredVisibleSpools = useMemo<RenderableSpool['name'][]>(() => {
    if (selectedSpools.length > 0) {
      return selectedSpools;
    }
    return [
      ...filteredUnassignedSpools.map((s) => s.name),
      ...filteredAssignedSpools.flatMap(([_, inner]) => inner.map((s) => s.name)),
    ].filter((s) => spoolVisibility[s]);
  }, [filteredUnassignedSpools, filteredAssignedSpools, selectedSpools, spoolVisibility]);

  useEffect(() => {
    if (requestingNewSpools || isNil(spools)) return;

    const spoolsToIsolate = getSpoolsFromSpoolNames(filteredVisibleSpools).flatMap(getDbIdsFromSpool);

    const spoolRequestsToIsolate =
      selectedSpools.length === 0
        ? filteredRequestedSpools
            .filter(([spoolId]) => visibleSpoolRequests.includes(spoolId))
            .flatMap(([, spoolRequest]) => getDbIdsFromSpool(spoolRequest))
        : [];

    commands?.isolate([...spoolsToIsolate, ...spoolRequestsToIsolate], shouldFitToView);
  }, [
    visibleSpoolRequests,
    filteredRequestedSpools,
    requestingNewSpools,
    selectedSpools,
    filteredVisibleSpools,
    shouldFitToView,
    getSpoolsFromSpoolNames,
    commands,
    spools,
  ]);

  if (isNil(model)) {
    return null;
  }

  const isLoading = loadingAssignedSpools || loadingSpoolRequests || loadingSpoolsReadyForFabrication || isNil(spools);

  if (isLoading || (spoolValues.length === 0 && spoolRequests?.length === 0)) {
    return (
      <Flex align="center" justify="center" flex={1}>
        {isLoading ? (
          <Loader type="dots" />
        ) : (
          spoolValues.length === 0 &&
          spoolRequests?.length === 0 && <Text c="dimmed">No spools found. Try a different view, or a new model.</Text>
        )}
      </Flex>
    );
  }

  return (
    <>
      <Stack p="md">
        <Flex gap="xs" align="center">
          <Text>Spools</Text>
          <a href={CloudSpoolingHelpLink} target="_blank" rel="noreferrer">
            <EvolveIcon icon="Help" color="primary" size={20} />
          </a>
        </Flex>
        <Flex gap="xs">
          <TextInputDebounced
            placeholder="Search..."
            value={searchPhrase}
            onChange={(v) => {
              setSearchPhrase(v);
              resetSelectedSpools();
            }}
            size="xs"
            flex={1}
          />
          <SpoolFiltersPopover
            spools={spoolValues}
            requestedSpools={spoolRequests}
            spoolFilter={spoolFilter}
            onFilterChanged={(f) => {
              setSpoolFilter(f);
              resetSelectedSpools();
            }}
          />
          <AssignSpoolToWorkOrder
            disabled={requestingNewSpools}
            spools={spoolValues.filter(({ name }) => selectedSpools.includes(name))}
            model={model}
            refresh={() => {
              void refreshSpools(model);
              resetSelectedSpools();
            }}
          />
        </Flex>
      </Stack>
      <Divider />
      <Stack gap={0} flex={1} style={{ overflowY: 'scroll' }}>
        <Accordion
          multiple
          defaultValue={['unassigned']}
          styles={{
            label: { fontWeight: 'bold' },
            content: { padding: 0 },
          }}
          mb="xs"
        >
          <RequestedSpools
            filteredRequestedSpools={filteredRequestedSpools}
            colorKeyEnabled={colorKeyEnabled}
            requestingNewSpools={requestingNewSpools}
          />
          <UnassignedSpools
            disabled={requestingNewSpools}
            filteredUnassignedSpools={filteredUnassignedSpools}
            colorKeyEnabled={colorKeyEnabled}
          />
          <AssignedSpools
            filteredAssignedSpools={filteredAssignedSpools}
            disabled={requestingNewSpools}
            colorKeyEnabled={colorKeyEnabled}
            refresh={() => {
              void refreshSpools(model);
              resetSelectedSpools();
            }}
          />
        </Accordion>
      </Stack>
    </>
  );
};
